import React from 'react';
import { DossierStep, useDossierContext, useDossierUpdateOrchestration } from 'common-app/dossier';
import { Spinner } from '@isyc-react/design/bootstrap';
import clsx from 'clsx';
import { getArchiveIcon } from '@isyc-react/archives/view/tools';
import { Slug } from '@isyc-react/animation/modules/base/trail';
import { DocGroupCardFile } from './doc-group';
import { DocumentalValidationCode } from '@isyc-react/types';
import { useNotificationHubContext } from '@isyc-react/form/modules/context';
import { nanoid } from 'nanoid';
import { NotificationItemType } from '@isyc-react/form/modules/base';
import { useTranslation } from 'react-i18next';
import { namespaces } from 'i18n/i18n.constants';
import { InputCheckbox } from '@isyc-react/form/modules/input/option';
const AcceptedFormat = props => {
    const { ext, children } = props;
    const classes = getArchiveIcon(ext);
    return (React.createElement("div", { className: 'd-flex align-items-center border-2 rounded-lg px-2 shadow-sm mx-2 bg-white opacity-80' },
        React.createElement("i", { className: clsx(classes.icon, 'font-size-22 me-2') }),
        React.createElement("div", { className: 'font-size-15' }, children)));
};
export const DossierDocuments = props => {
    const { dossier } = useDossierContext();
    const { docs, onUpload, businessUnitId } = props;
    const [updateStep, { loading }] = useDossierUpdateOrchestration();
    const buttonRefs = [];
    const { addNotifications } = useNotificationHubContext();
    const [isUploading, setIsUploading] = React.useState(false);
    const [buttonWasClicked, setButtonWasClicked] = React.useState(false);
    const [uploadingFiles, setUploadingFiles] = React.useState(0);
    const [checkValue, setCheckValue] = React.useState({
        checked: false,
    });
    const uploadingFilesRef = React.useRef(0);
    const { t } = useTranslation(namespaces.common);
    const nPending = React.useMemo(() => {
        if (docs) {
            const nDocGroupPending = docs.pending.reduce((acc, p) => acc + p.requests.length, 0);
            return nDocGroupPending;
        }
        return 0;
    }, [docs]);
    const addUploadingFiles = React.useCallback((num) => {
        uploadingFilesRef.current += num;
        setUploadingFiles(uploadingFilesRef.current);
    }, [setUploadingFiles]);
    const removeUploadingFiles = React.useCallback((num) => {
        uploadingFilesRef.current -= num;
        setUploadingFiles(uploadingFilesRef.current);
    }, [setUploadingFiles]);
    const nRealPending = React.useMemo(() => {
        if (docs) {
            const nDocGroupPending = docs.pending
                .filter(doc => doc.validation !== DocumentalValidationCode.NONE)
                .reduce((acc, p) => acc + p.requests.length, 0);
            return nDocGroupPending;
        }
        return 0;
    }, [docs]);
    const nCompleted = React.useMemo(() => {
        if (docs) {
            return docs.completed.reduce((acc, c) => acc + c.requests.length, 0);
        }
        return 0;
    }, [docs]);
    const nTotal = nPending + nCompleted;
    const onFinishToUpload = () => {
        removeUploadingFiles(1);
    };
    const onClickButtonsUpload = React.useCallback(() => {
        setIsUploading(true);
        const pendingUpload = buttonRefs.filter(ref => ref.current && !ref.current.disabled);
        if (pendingUpload.length > 0) {
            addUploadingFiles(pendingUpload.length);
            pendingUpload.forEach((btn, i) => {
                // btn.current?.click()
                setTimeout(() => {
                    var _a;
                    // const startTime = performance.now()
                    (_a = btn.current) === null || _a === void 0 ? void 0 : _a.click();
                    // const endTime = performance.now()
                }, 750);
            });
        }
        else if (nRealPending !== 0) {
            addNotifications([
                {
                    key: nanoid(),
                    message: `Aucun fichier n'a été sélectionné pour le chargement`,
                    type: NotificationItemType.ERROR,
                },
            ]);
        }
        addNotifications([
            {
                key: nanoid(),
                message: `Vérifiez si vous avez d'autres fichiers à charger`,
                type: NotificationItemType.INFO,
            },
        ]);
        setButtonWasClicked(true);
    }, [setIsUploading, addNotifications]);
    React.useEffect(() => {
        if (uploadingFiles === 0 && isUploading) {
            setIsUploading(false);
        }
    }, [uploadingFiles, isUploading]);
    React.useEffect(() => {
        if (nRealPending === 0 && uploadingFiles === 0 && (buttonWasClicked || nPending === 0)) {
            updateStep({ variables: { dossierId: dossier === null || dossier === void 0 ? void 0 : dossier.id, step: DossierStep.VALIDATED } });
        }
        // else {
        //   addNotifications([
        //     {
        //       key: nanoid(),
        //       message: `Vérifiez si vous avez d'autres fichiers à charger`,
        //       type: NotificationItemType.INFO,
        //     },
        //   ])
        // }
    }, [nRealPending, nPending, buttonWasClicked]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'px-5 px-md-0' },
            React.createElement("h1", { className: 'text-black fw-600 font-size-20 pt-5 pt-md-0' }, "Transmettre vos justificatifs"),
            React.createElement("p", { className: 'mb-0' }, "Vous devez nous transmettre des justificatifs obligatoires relatifs aux informations renseign\u00E9es. Apr\u00E8s t\u00E9l\u00E9chargement de ces justificatifs, vous pourrez valider votre enqu\u00EAte en bas de page. Sans validation de votre part, votre enqu\u00EAte ne sera pas trait\u00E9e."),
            React.createElement("p", { className: 'mb-0' }, "Si vous effectuez votre d\u00E9claration depuis un smartphone ou une tablette, vous pouvez prendre vos justificatifs directement en photo puis les t\u00E9l\u00E9charger."),
            React.createElement("div", { className: 'p-0 bg-white mt-4 mb-4 mx-0' },
                React.createElement("h2", { className: 'bg-tertiary text-center text-black fw-700 py-2 font-size-16 text-uppercase mb-5' }, "Justificatifs attendus"),
                React.createElement("div", { className: 'd-flex justify-content-center' },
                    React.createElement(DocGroupPendingList, Object.assign({}, { docs, businessUnitId, onUpload }))),
                React.createElement("div", { className: 'mb-5 mx-auto' },
                    React.createElement("p", { className: 'mb-1 font-size-15 text-center' }, "Les formats accept\u00E9s sont les suivants. La taille du document doit \u00EAtre de 5 Mo maximum."),
                    React.createElement("div", { className: 'd-flex justify-content-center' },
                        React.createElement(AcceptedFormat, { ext: '.jpeg' }, "jpeg"),
                        React.createElement(AcceptedFormat, { ext: '.png' }, "png"),
                        React.createElement(AcceptedFormat, { ext: '.pdf' }, "pdf"),
                        React.createElement(AcceptedFormat, { ext: '.tiff' }, "tiff"))),
                React.createElement("div", { className: 'mb-5 mx-auto' },
                    React.createElement(DocRequestByPerson, Object.assign({}, { docs, businessUnitId, onUpload, buttonUploadRef: buttonRefs, onFinish: onFinishToUpload }))),
                React.createElement("div", { className: 'mb-5 mx-auto' },
                    React.createElement("p", { className: 'mb-1 font-size-15' },
                        React.createElement("span", { className: 'text-danger me-2' }, "*"),
                        "Documents obligatoires."))),
            React.createElement("div", { className: 'd-flex align-items-end' },
                React.createElement(InputCheckbox, { label: t('batigere.documents.check.confirm'), value: checkValue, onChange: e => {
                        setCheckValue(e);
                    } })),
            React.createElement("div", { className: 'd-flex align-items-center justify-content-between mt-4 flex-column-reverse flex-sm-row gap-3 buttons mb-3' },
                React.createElement("button", { onClick: () => updateStep({ variables: { dossierId: dossier === null || dossier === void 0 ? void 0 : dossier.id, step: DossierStep.EXPLICATIONS } }), type: 'button', className: 'btn btn-link btn-lg', disabled: loading },
                    React.createElement("div", { className: 'd-flex align-items-center justify-content-center' },
                        loading ? (React.createElement("div", { className: 'd-flex align-items-center justify-content-center' },
                            React.createElement(Spinner, { animation: 'border', className: 'text-dark-primary', size: 'sm' }))) : (React.createElement("i", { className: 'text-dark-primary mdi mdi-chevron-left' })),
                        React.createElement("div", { className: 'ms-3' }, "Retour"))),
                React.createElement("button", { onClick: () => {
                        onClickButtonsUpload();
                        //updateStep({ variables: { dossierId: dossier?.id, step: DossierStep.VALIDATED } })
                    }, type: 'button', className: 'btn btn-secondary btn-lg fw-500', disabled: loading || isUploading || (checkValue === null || checkValue === void 0 ? void 0 : checkValue.checked) !== true },
                    React.createElement("div", { className: 'd-flex align-items-center justify-content-center' },
                        React.createElement("div", { className: 'me-2' }, "Valider les documents"),
                        loading ? (React.createElement(Spinner, { animation: 'border', size: 'sm' })) : (React.createElement("i", { className: 'mdi mdi-check text-white font-size-16 d-block' }))))),
            React.createElement("span", null, t('bailleurs.footer.card')))));
};
const DocRequest = props => {
    const { docs, businessUnitId, onUpload, buttonUploadRef, onFinish } = props;
    return (React.createElement("div", { className: 'p-3' }, docs.pending.map((doc, i) => (React.createElement(React.Fragment, { key: `${doc.code}-${doc.relatedId}-${doc.relatedObjectTypeId}` },
        React.createElement(Slug, { delay: 0, from: {
                opacity: 0,
                transform: 'translate3d(-30px,-30px,0)',
            } }),
        React.createElement("div", { className: 'row justify-content-center' },
            React.createElement(Slug, { delay: 0, from: { opacity: 0, transform: 'translate3d(-40px,-60px,0)' }, className: 'col-12 col-lg-6 mb-4 pb-2' }, doc.requests.map((req, j) => (React.createElement(DocGroupCardFile, { key: `${doc.code}-${doc.relatedId}-${doc.relatedObjectTypeId}-${req.tCode}`, relatedId: doc.relatedId, relatedObjectTypeId: doc.relatedObjectTypeId, request: req, obligatory: doc.validation !== DocumentalValidationCode.NONE, businessUnitId: businessUnitId, onSuccess: onUpload, buttonUploadRef: buttonUploadRef, onFinish: onFinish }))))))))));
};
const DocGroupPendingList = props => {
    const { docs } = props;
    const relatedObjectDocs = docs.pending.reduce((acc, doc) => {
        if (!acc[doc.relatedId]) {
            acc[doc.relatedId] = [];
        }
        acc[doc.relatedId].push(doc);
        return acc;
    }, {});
    return (React.createElement("ul", { className: 'list-group mb-3' }, Object.keys(relatedObjectDocs).map((relatedId, i) => {
        return (React.createElement(React.Fragment, { key: relatedId }, relatedObjectDocs[relatedId].map((element) => {
            const lastCharPosition = element.name.lastIndexOf('-');
            const replaceName = element.name.slice(0, lastCharPosition) + ':' + element.name.slice(lastCharPosition + 1);
            return (React.createElement(React.Fragment, { key: element.code },
                React.createElement("li", null, replaceName)));
        })));
    })));
};
const DocRequestByPerson = props => {
    const { docs, businessUnitId, onUpload, buttonUploadRef, onFinish } = props;
    const relatedObjectDocs = docs.pending.reduce((acc, doc) => {
        if (!acc[doc.relatedId]) {
            acc[doc.relatedId] = [];
        }
        acc[doc.relatedId].push(doc);
        return acc;
    }, {});
    return (React.createElement("div", null, Object.keys(relatedObjectDocs).map((relatedId, i) => (React.createElement(React.Fragment, { key: relatedId },
        React.createElement("h4", { className: 'text-black text-center font-size-16' }, relatedObjectDocs[relatedId][0].objectName),
        React.createElement(DocRequest, Object.assign({}, {
            docs: { pending: relatedObjectDocs[relatedId], completed: [] },
            businessUnitId,
            onUpload,
            buttonUploadRef,
            onFinish,
        })))))));
};
